import { css } from '@emotion/react';
import { faEnvelope } from '@fortawesome/pro-regular-svg-icons/faEnvelope';
import { faLockAlt } from '@fortawesome/pro-solid-svg-icons';
import { type FC } from 'react';
import {
  ButtonSize, ButtonStyle,
} from '~/_shared/baseComponents/buttons/button/button.types';
import { CheckboxComponent } from '~/_shared/baseComponents/checkbox';
import { TextInputAuthComponent } from '~/_shared/baseComponents/inputs';
import { TooltipComponent } from '~/_shared/baseComponents/tooltip/tooltip.component';
import { FormErrorMessageComponent } from '~/_shared/components/formErrorMessage/formErrorMessage.component';
import { useTranslation } from '~/_shared/utils/hooks';
import { refreshTokenValidityInDays } from '~/authorization/authContext/auth.utils';
import { inputWrapperStyle } from '~/authorization/loginPage/loginForm/loginForm.component';
import { authorizationPagesErrorStyle } from '../../authorizationPages.styles';
import {
  AuthorizationButtonComponent, AuthorizationSubmitButtonComponent,
} from '../../submitButton/authorizationSubmitButton.component';
import { LoginFormLinksComponent } from './loginFormLinks.component';
import { type FormField } from './loginFormOptionsView.component';
import { UserLoginErrorComponent } from './userLoginError.component';

const rememberMeCheckboxStyle = css({ margin: '15px 0' });

const backButtonWrapperStyle = css({ marginTop: 24 });

type LoginFormEmailViewProps = Readonly<{
  email: FormField;
  password: FormField;
  isLoading: boolean;
  isFormValid: boolean;
  rememberMe: boolean;
  showError: boolean;
  error: string;
  userEmail: string;
  onToggleRememberMe: () => void;
  onBack: () => void;
}>;

export const LoginFormEmailView: FC<LoginFormEmailViewProps> = (props) => {
  const [t] = useTranslation();

  return (
    <>
      <div css={inputWrapperStyle}>
        <TextInputAuthComponent
          autoComplete="username"
          hasError={props.email.errors.length > 0}
          icon={faEnvelope}
          isDisabled={props.isLoading}
          name="email"
          onBlur={props.email.onBlur}
          onChange={props.email.onChange}
          placeholder={t('Email')}
          type="email"
          value={props.email.value}
        />

        {props.email.errors.length > 0 && (
          <FormErrorMessageComponent
            css={authorizationPagesErrorStyle}
            messages={props.email.errors}
          />
        )}
      </div>

      <div css={inputWrapperStyle}>
        <TextInputAuthComponent
          autoComplete="current-password"
          icon={faLockAlt}
          name="password"
          placeholder={t('Password')}
          type="password"
          value={props.password.value}
          onChange={props.password.onChange}
          onBlur={props.password.onBlur}
          hasError={props.password.errors.length > 0}
          isDisabled={props.isLoading}
        />

        {props.password.errors.length > 0 && (
          <FormErrorMessageComponent
            css={authorizationPagesErrorStyle}
            messages={props.password.errors}
          />
        )}
      </div>

      <div css={rememberMeCheckboxStyle}>
        <CheckboxComponent
          checkedSetter={props.onToggleRememberMe}
          isChecked={props.rememberMe}
          isDisabled={props.isLoading}
          text={t('Remember me', { numberOfDays: refreshTokenValidityInDays })}
          textStyle={css({ fontSize: 18 })}
        />
      </div>

      <TooltipComponent tooltipContent={props.isFormValid ? undefined : t('login.validation.validEmailAndPassword')}>
        <span css={{ display: 'block' }}>
          <AuthorizationSubmitButtonComponent
            data-testid="login-submit"
            isLoading={props.isLoading}
            isDisabled={!props.isFormValid || props.isLoading}
          >
            {t('Sign in')}
          </AuthorizationSubmitButtonComponent>
        </span>
      </TooltipComponent>

      {props.showError && (
        <UserLoginErrorComponent
          code={props.error}
          userEmail={props.userEmail}
        />
      )}

      <LoginFormLinksComponent isLoading={props.isLoading} />

      <div css={backButtonWrapperStyle}>
        <AuthorizationButtonComponent
          buttonStyle={ButtonStyle.Secondary}
          size={ButtonSize.Medium}
          isLoading={false}
          isDisabled={props.isLoading}
          onClick={props.onBack}
        >
          {t('login.email.signInAnotherWay')}
        </AuthorizationButtonComponent>
      </div>
    </>
  );
};
